import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { useRouteMatch } from 'react-router-dom';
import { trackEvent } from 'utils/analytics';

import { SpotInterface } from 'types';
import { getSpotListAction, logoutAction, updateIsDemoModeAction } from 'actions/actions';
import { RETOOL_SPOT_FORM_URL } from 'utils/constants';
import { isActiveTab, scrollMainToTop } from 'utils/helpers';
import { AppState } from '../../reducers/reducers';
import { selectedSpotInfo } from '../../selectors';

// Icons
import HouseIcon from 'assets/icons/house.svg?react';
import PeopleIcon from 'assets/icons/people.svg?react';
import PayIcon from 'assets/icons/pay.svg?react';
import FileIcon from 'assets/icons/file.svg?react';
import SupportIcon from 'assets/icons/support.svg?react';
import AddIcon from 'assets/icons/add.svg?react';
import InvoiceIcon from 'assets/icons/invoice.svg?react';
import FileDocIcon from 'assets/icons/fileDoc.svg?react';

import { AuthorizedView, Roles } from 'components/Utilities/AuthorizedView';
import Footer from 'components/Footer/Footer';

import SpotMenu from './SpotMenu';
import SidebarItem from './SidebarItem';
import SidebarSection from './SidebarSection';
import styles from './Sidebar.module.css';
import SidebarHeader from './Header';
import SidebarControls from './Controls';

type SidebarProps = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const MIN_WIDTH_FOR_EXPANDED_MENU = 960;

function Sidebar({
    orgSpots,
    spotsLoading,
    spotList,
    children: pageContent,
    superuser,
    organization,
    orgSpotsLoading,
    selectedSpot,
    isDemoMode,
    push,
    logout,
    profile,
    updateIsDemoMode,
    getSpotList,
}: PropsWithChildren<SidebarProps>) {
    const [isExpanded, setIsExpanded] = useState(true);
    const [lastSelectedSpot, setLastSelectedSpot] = useState<SpotInterface | null>(selectedSpot);
    const isChatPage = useRouteMatch('/organization/:orgId/spots/:spotId/chat');

    const orgUrl = useMemo(() => {
        return organization ? `/organization/${organization.pk}/` : '';
    }, [organization]);

    const spotUrl = useMemo(() => {
        return lastSelectedSpot && orgUrl ? `${orgUrl}spots/${lastSelectedSpot.pk}/` : '';
    }, [orgUrl, lastSelectedSpot]);

    const navigateToTab = useCallback(
        (tabUrl: string) => {
            push(tabUrl);
            trackEvent('Navigate to', {
                orgPk: organization?.pk ?? '',
                name: tabUrl,
                spotPk: lastSelectedSpot?.pk ?? '',
            });
            scrollMainToTop();
        },
        [push, lastSelectedSpot, organization]
    );

    const orgSpotsList = useMemo(() => {
        if (!orgSpotsLoading && orgSpots && Object.keys(orgSpots).length > 0) {
            return Object.values(orgSpots).map((spot) => ({ label: spot.name, value: String(spot.pk), key: spot.pk }));
        }
        return [];
    }, [orgSpotsLoading, orgSpots]);

    const handleLogout = useCallback(() => {
        trackEvent('Clicked log out');
        logout();
        push('/login');
    }, [logout, push]);

    const toggleExpanded = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            setIsExpanded(!isExpanded);
        },
        [isExpanded, setIsExpanded]
    );

    const updateDimensions = useCallback(() => {
        const width = window.innerWidth;
        if (width > MIN_WIDTH_FOR_EXPANDED_MENU) {
            setIsExpanded(true);
        } else {
            setIsExpanded(false);
        }
    }, [setIsExpanded]);

    useEffect(() => {
        updateDimensions();
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
    }, []);

    useEffect(() => {
        getSpotList();
    }, [getSpotList]);

    useEffect(() => {
        if (selectedSpot) setLastSelectedSpot(selectedSpot);
    }, [selectedSpot, setLastSelectedSpot]);

    // Set sidebar width CSS variable on document root for modals and fixed-position elements
    useEffect(() => {
        const sidebarWidth = isExpanded ? '260px' : '68px';
        document.documentElement.style.setProperty('--sidebar-width', sidebarWidth);
        return () => {
            document.documentElement.style.removeProperty('--sidebar-width');
        };
    }, [isExpanded]);

    return (
        <div
            className={`${styles.sidebarContainer} ${isExpanded ? styles.expanded : styles.collapsed}`}
            id={styles.agSidebar}
        >
            {/* Spacer to push content */}
            <div className={styles.sidebarSpacer} />

            {/* Sidebar */}
            <nav className={styles.sidebar} aria-label="navigation">
                {/* Header with logo and toggle */}
                <SidebarHeader toggleExpanded={toggleExpanded} profile={profile} handleLogout={handleLogout} />

                {/* Search bar and controls */}
                <SidebarControls
                    spotsLoading={spotsLoading}
                    spotList={spotList}
                    superuser={superuser}
                    organization={organization}
                    selectedSpot={lastSelectedSpot}
                    orgSpotsList={orgSpotsList}
                    navigateToTab={navigateToTab}
                    setIsExpanded={setIsExpanded}
                    isExpanded={isExpanded}
                    orgUrl={orgUrl}
                    isDemoMode={isDemoMode}
                    updateIsDemoMode={updateIsDemoMode}
                />

                {/* Navigation items */}
                {organization && (
                    <div className={styles.nav}>
                        <SidebarItem
                            label="Overview"
                            icon={<HouseIcon />}
                            active={isActiveTab('detail')}
                            onClick={() => navigateToTab(`${orgUrl}spots`)}
                            isExpanded={isExpanded}
                        />

                        {lastSelectedSpot && (
                            <SpotMenu
                                selectedSpot={lastSelectedSpot}
                                baseUrl={spotUrl}
                                navigateToTab={navigateToTab}
                                isExpanded={isExpanded}
                            />
                        )}

                        <AuthorizedView allowed={Roles.Admin}>
                            <SidebarSection title="Admin Tools" isExpanded={isExpanded}>
                                <SidebarItem
                                    label="Team"
                                    icon={<PeopleIcon />}
                                    active={isActiveTab('team')}
                                    isExpanded={isExpanded}
                                    onClick={() => navigateToTab(`${orgUrl}team`)}
                                />
                                <SidebarItem
                                    label="Payouts"
                                    icon={<PayIcon />}
                                    active={isActiveTab('payouts')}
                                    isExpanded={isExpanded}
                                    onClick={() => navigateToTab(`${orgUrl}payouts`)}
                                />
                                <SidebarItem
                                    label="Income Statement"
                                    icon={<FileIcon />}
                                    active={isActiveTab('income-statement')}
                                    isExpanded={isExpanded}
                                    onClick={() => navigateToTab(`${orgUrl}income-statement`)}
                                />
                                <SidebarItem
                                    label="Documents"
                                    icon={<FileDocIcon />}
                                    active={isActiveTab('documents')}
                                    isExpanded={isExpanded}
                                    onClick={() => navigateToTab(`${orgUrl}documents`)}
                                />
                            </SidebarSection>
                        </AuthorizedView>
                    </div>
                )}

                {/* Footer */}
                <div className={styles.footer}>
                    <ul>
                        <SidebarItem
                            label="Get Help"
                            icon={<SupportIcon />}
                            isExpanded={isExpanded}
                            onClick={() => navigateToTab('/help-center')}
                        />
                        <AuthorizedView allowed={Roles.Superuser}>
                            <SidebarItem
                                label="Add New Organization"
                                icon={<AddIcon />}
                                isExpanded={isExpanded}
                                onClick={() => window.open(RETOOL_SPOT_FORM_URL, '_blank')}
                            />
                            <SidebarItem
                                label="Invoices"
                                icon={<InvoiceIcon />}
                                disabled={!spotUrl}
                                isExpanded={isExpanded}
                                onClick={() => navigateToTab(`${spotUrl}invoice`)}
                            />
                        </AuthorizedView>
                    </ul>
                </div>
            </nav>

            {/* Main content */}
            <main className={styles.mainContent}>
                <div className={isChatPage ? undefined : styles.pageContent}>{pageContent}</div>
                {!isChatPage && <Footer />}
            </main>
        </div>
    );
}

function mapStateToProps(state: AppState) {
    return {
        spotList: state.spots.spotList,
        spotsLoading: state.spots.spotListLoading,
        selectedSpot: selectedSpotInfo(state),
        orgSpots: state.spots.orgSpots,
        orgSpotsLoading: state.spots.orgSpotsLoading,
        organization: state.organization.organization,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        superuser: state.auth.profile!.superuser,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        profile: state.auth.profile!,
        isDemoMode: state.dashboard.isDemoMode,
    };
}

const mapDispatchToProps = {
    updateIsDemoMode: updateIsDemoModeAction,
    getSpotList: getSpotListAction,
    logout: logoutAction,
    push,
};

export default connect(mapStateToProps, mapDispatchToProps)(Sidebar);
